import { ImportManager } from '@carboncopyco/components';
import {
  AITool,
  ChangesTrackerTool,
  CustomChartBuilderTool,
  CustomCodeTool,
  FindAndReplaceManagerTool,
  GuidanceTool,
  ImportManagerTool,
  NavigationManagerTool,
  ObjectsTool,
  PerformanceTool,
  SpotEditsTool,
  TransactionsManagerTool,
  VariablesManagerTool,
} from '@carboncopyco/editor-sdk';
import type { IEditorDraft } from '@carboncopyco/types';
import type { IGlobalTab } from '@carboncopyco/types/desktop';
import { type ReactNode, useState } from 'react';
import { useFlagsFallback } from '../../hooks/useFlagsFallback';
import { useFileServiceContext } from '../../store/context/FileServiceContext';
import { DemoLockedModal } from './DemoLockedModal';
import type { EditorModalName } from './types';

const importManagerToolLearnMoreUrl =
  'https://help.demostack.com/en/articles/8626304-import-edits';

interface IEditorModalsProps {
  tab: IGlobalTab;
  draft?: IEditorDraft;
  isInnerTabLoaded: boolean;
  organizationId: string;
  onExit: () => void;
  isOpenModal: (modal: EditorModalName) => boolean;
  closeModal: (modal: EditorModalName) => void;
}

const Backdrop = ({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) => (
  <div className="absolute inset-0 grid items-center justify-center bg-black/50">
    <div
      className="absolute inset-0"
      onClick={onClick}
    />
    <div className="absolute inset-0 pointer-events-none [&>*]:pointer-events-auto">
      {children}
    </div>
  </div>
);

export const EditorModals = ({
  tab,
  draft,
  isInnerTabLoaded,
  organizationId,
  onExit,
  isOpenModal,
  closeModal,
}: IEditorModalsProps) => {
  const flags = useFlagsFallback();

  const [innerHtml, setInnerHtml] = useState('');

  const getInnerHtml = async () => {
    const html = await window.dsDesktop.getInnerHtml();
    setInnerHtml(html);
  };

  const { downloadCsvFile } = useFileServiceContext();

  const { demo } = tab.payload;
  return (
    <div className="absolute inset-0 pointer-events-none [&>*]:pointer-events-auto overflow-hidden">
      {isOpenModal('customCharts') && (
        <Backdrop onClick={() => closeModal('customCharts')}>
          {/* -ml-[60px] (a size of sidebar) - Because of measurements inside CustomChartBuilderTool and difference between web and desktop layout */}
          <div className="-ml-[60px]">
            <CustomChartBuilderTool />
          </div>
        </Backdrop>
      )}
      {isOpenModal('changeTracker') && (
        <Backdrop onClick={() => closeModal('changeTracker')}>
          <ChangesTrackerTool />
        </Backdrop>
      )}
      <VariablesManagerTool />
      <FindAndReplaceManagerTool />
      <CustomCodeTool />
      <TransactionsManagerTool />
      <NavigationManagerTool />
      <ImportManagerTool>
        <ImportManager
          draftId={String(draft?.id)}
          organizationId={String(demo.organizationId)}
          workspaceId={String(demo.workspaceId)}
          currentDemoId={String(demo.id)}
          onLearnMore={() =>
            window.dsDesktop.shellOpenExternal(importManagerToolLearnMoreUrl)
          }
        />
      </ImportManagerTool>
      <AITool
        innerHtml={innerHtml}
        onGetInnerHtml={getInnerHtml}
        getImage={window.dsDesktop.capturePage}
      />
      <SpotEditsTool />
      <ObjectsTool />
      <GuidanceTool />
      {flags.editorPerformance && (
        <PerformanceTool
          onDownloadClick={(csvContent) => {
            downloadCsvFile(csvContent, `${tab.title} - Performance results`);
          }}
        />
      )}

      {flags.editorMultiUserLocking && demo && draft && isInnerTabLoaded && (
        <DemoLockedModal
          tab={tab}
          demoId={demo.id}
          organizationId={organizationId}
          onExit={onExit}
        />
      )}
    </div>
  );
};
